'use client'

import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribe to a CSS media query.
 *
 * The design project decided its layout once, from `window.innerWidth > 720`,
 * at first render. Reading it through `matchMedia` keeps the exact same
 * breakpoint and both layouts pixel-identical, but also survives a resize or
 * an orientation change — which a production page has to do.
 */
export function useMediaQuery(query) {
  const subscribe = useCallback(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    [query],
  )

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  }, [query])

  // Server snapshot: mobile-first, matching the design's 390px primary layout.
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/** Desktop layout starts above 720px — the breakpoint used in the design. */
export const DESKTOP_QUERY = '(min-width: 721px)'

export function useIsDesktop() {
  return useMediaQuery(DESKTOP_QUERY)
}
